import React from 'react';
import HlsVideoPlayer from './HlsVideoPlayer';
import { getStore, usePlayerStore } from '../../../stores/StoreContext';
import { config } from '../../../config';
import VideoRenderer from '../VideoRenderer';
import HoverControls from '../Controls/HoverControls';
import Overlays from '../Overlays';
import { View } from '@smarterservices/smarter-ui';
import { usePlayerHotkeys } from '../hooks/usePlayerHotkeys';
import { useSmartSpeed } from '../hooks/useSmartSpeed';
import CONSTANTS from '../../../config/constants';

function HlsMedia() {
  const PlayerStore = getStore('playerStore');
  const ExamSessionStore = getStore('examSessionStore');

  const canHaveLiveVideo = usePlayerStore(
    (state) => state.canHaveLiveVideo
  );
  const viewRawHlsVideo = usePlayerStore(
    (state) => state.viewRawHlsVideo
  );

  // Initialize player hotkeys and smart speed for raw video mode
  usePlayerHotkeys();
  useSmartSpeed();

  // Centralized callbacks for HLS player
  const handleBufferingStart = ({ bufferAhead, speed, targetBuffer }) => {
    // Only show buffering overlay if no higher-priority overlay is active
    const currentOverlay = PlayerStore.overlay;
    if (!currentOverlay || currentOverlay === CONSTANTS.OVERLAYS.BUFFERING) {
      PlayerStore.setOverlay(CONSTANTS.OVERLAYS.BUFFERING);
    }
  };

  const handleBufferingEnd = ({ bufferAhead, speed }) => {
    // Only clear if overlay is still BUFFERING — avoid nuking overlays set by other systems
    const currentOverlay = PlayerStore.overlay;
    if (currentOverlay === CONSTANTS.OVERLAYS.BUFFERING) {
      PlayerStore.setOverlay(null);
    }
  };

  const handleOnPlaying = ({ currentTime, playbackRate }) => {
    // Only clear the BUFFERING overlay — not other overlays like PLAYBACK_COVER or PLAYBACK_COMPLETE
    // This is a safety net for stuck buffer overlays, not a general overlay dismissal
    const currentOverlay = PlayerStore.overlay;
    if (currentOverlay === CONSTANTS.OVERLAYS.BUFFERING) {
      PlayerStore.setOverlay(null);
    }
  };

  const handleSuggestedSpeedChange = ({ suggestedSpeed, currentSpeed, previousSuggestion, metrics }) => {
    if (suggestedSpeed !== currentSpeed) {
      PlayerStore.setSuggestedPlaybackSpeed(suggestedSpeed);
    }
  };

  return (
    <View
      as="div"
      className={viewRawHlsVideo ? "raw-video-wrapper" : "integrated-video-wrapper"}
      position="relative"
      width="100%"
      height="100%"
      overflow="hidden"
      onMouseEnter={() => viewRawHlsVideo && PlayerStore.setShowHoverControls(true)}
      onMouseLeave={() => viewRawHlsVideo && PlayerStore.setShowHoverControls(false)}
    >
      {/* HLS video player - always rendered in same location to preserve playback position */}
      <HlsVideoPlayer
        url={`${config.media.playback.baseUrl}/v1/${ExamSessionStore.getHlsAuthenticationToken()}/installs/${ExamSessionStore.installSid}/exams/${ExamSessionStore.examSid}/sessions/${ExamSessionStore.sessionSid}/playlist.m3u8`}
        showRawVideo={viewRawHlsVideo}
        onBufferingStart={handleBufferingStart}
        onBufferingEnd={handleBufferingEnd}
        onSuggestedSpeedChange={handleSuggestedSpeedChange}
        onPlaying={handleOnPlaying}
      />

      {/* Only show integrated VideoRenderer if not viewing raw HLS video */}
      {!canHaveLiveVideo && !viewRawHlsVideo && (
        <VideoRenderer />
      )}

      {/* Raw video controls and overlays - only shown in raw mode */}
      {viewRawHlsVideo && (
        <>
          <HoverControls />
          <View as="div" textAlign="center" width="100%" height="100%">
            <Overlays />
          </View>
        </>
      )}
    </View>
  );
}

export default HlsMedia;
